/**
 * Schedules a MusicPiece through PianoSynthEngine using AudioContext clock.
 */

import type { MusicPiece, NoteEvent } from "./types.js";
import { pieceDurationBeats } from "./library.js";

export interface PiecePlayerSynth {
  unlockSync: () => void;
  noteOn: (midi: number, velocity?: number, when?: number) => void;
  noteOff: (midi: number, velocity?: number, when?: number) => void;
  audioContext: AudioContext | null;
}

export type PiecePlayerEvent =
  | { type: "start"; pieceId: string }
  | { type: "note"; note: NoteEvent; audioTime: number }
  | { type: "end"; pieceId: string };

export type PiecePlayerHandler = (e: PiecePlayerEvent) => void;

export class PiecePlayer {
  private synth: PiecePlayerSynth;
  private handlers = new Set<PiecePlayerHandler>();
  private timer: ReturnType<typeof setInterval> | null = null;
  private playing = false;
  private piece: MusicPiece | null = null;
  private startAudioTime = 0;
  private secondsPerBeat = 0.5;
  private cursor = 0;
  private sorted: NoteEvent[] = [];

  constructor(synth: PiecePlayerSynth) {
    this.synth = synth;
  }

  get isPlaying(): boolean {
    return this.playing;
  }

  get currentPiece(): MusicPiece | null {
    return this.piece;
  }

  onEvent(handler: PiecePlayerHandler): () => void {
    this.handlers.add(handler);
    return () => this.handlers.delete(handler);
  }

  play(piece: MusicPiece, bpmOverride?: number): void {
    this.stop(/*silent*/ true);
    this.synth.unlockSync();
    const ctx = this.synth.audioContext;
    if (!ctx) throw new Error("AudioContext required to play piece");

    if (ctx.state === "suspended") void ctx.resume();

    this.piece = piece;
    this.sorted = [...piece.notes].sort((a, b) => a.beat - b.beat);
    this.cursor = 0;
    const bpm = bpmOverride ?? piece.bpm;
    this.secondsPerBeat = 60 / bpm;
    this.startAudioTime = ctx.currentTime + 0.05;
    this.playing = true;
    this.emit({ type: "start", pieceId: piece.id });

    this.timer = setInterval(() => this.tick(), 20);
    this.tick();
  }

  /** Play until natural end or stop(). */
  playAsync(piece: MusicPiece, bpmOverride?: number): Promise<void> {
    return new Promise((resolve) => {
      const off = this.onEvent((e) => {
        if (e.type === "end" && e.pieceId === piece.id) {
          off();
          resolve();
        }
      });
      try {
        this.play(piece, bpmOverride);
      } catch (err) {
        off();
        throw err;
      }
    });
  }

  stop(silent = false): void {
    if (this.timer != null) {
      clearInterval(this.timer);
      this.timer = null;
    }
    const wasPlaying = this.playing;
    const pieceId = this.piece?.id;
    this.playing = false;
    this.piece = null;
    this.sorted = [];
    this.cursor = 0;
    if (wasPlaying && pieceId && !silent) {
      this.emit({ type: "end", pieceId });
    }
  }

  private tick(): void {
    if (!this.playing || !this.piece) return;
    const ctx = this.synth.audioContext;
    if (!ctx) return;

    const lookAhead = 0.12;
    const horizon = ctx.currentTime + lookAhead;
    const totalBeats = pieceDurationBeats(this.piece);

    while (this.cursor < this.sorted.length) {
      const note = this.sorted[this.cursor];
      const onTime = this.startAudioTime + note.beat * this.secondsPerBeat;
      if (onTime > horizon) break;

      const vel = note.velocity ?? 90;
      this.synth.noteOn(note.midi, vel, onTime);
      const offTime = onTime + note.duration * this.secondsPerBeat;
      this.synth.noteOff(note.midi, 0, offTime);
      this.emit({ type: "note", note, audioTime: onTime });
      this.cursor += 1;
    }

    const endTime = this.startAudioTime + totalBeats * this.secondsPerBeat + 0.05;
    if (this.cursor >= this.sorted.length && ctx.currentTime >= endTime) {
      this.stop();
    }
  }

  private emit(e: PiecePlayerEvent): void {
    for (const h of this.handlers) h(e);
  }
}
